import { Card, Col, Divider, Empty, Row, Skeleton, Table } from 'antd';
import moment from 'moment';
import { useRouteMatch } from 'react-router-dom';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ChartPie } from '@/components/_common';
import SyntheticAnalysisFilter from '@/components/CRM/SearchQuestionnaire/SyntheticAnalysisFilter';
import { useAnswersSyntheticResearch } from '@/core/domain/crm/Answers/Answers.hooks';
import {
  CommentBoxResults,
  MultipleChoiceResponsesResults,
  StarClassificationResults,
} from '@/core/domain/crm/Answers/Answers.types';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';

const SyntheticAnalysisPage: React.FC = () => {
  const intl = useIntl();
  const { quizId } = useRouteMatch<{ quizId: string }>().params;
  const [filter, setFilter] = useState({
    startDate: moment(),
    endDate: moment(),
  });

  const { data, isLoading } = useAnswersSyntheticResearch(Number(quizId), {
    startReplyDate: filter.startDate.format('YYYY-MM-DD'),
    endReplyDate: filter.endDate.format('YYYY-MM-DD'),
  });

  function renderMultipleChoices(list: MultipleChoiceResponsesResults[]) {
    return list.map((item) => (
      <Col span={24} md={8} key={`multiple-${item.question.id}`}>
        <Card title={item.question.description}>
          <ChartPie
            data={item.detailsSeletecOptions.map((detail) => ({
              id: detail.optionsDescription,
              label: detail.optionsDescription,
              value: detail.quantitySelected,
            }))}
          />
        </Card>
      </Col>
    ));
  }

  function renderClassification(list: StarClassificationResults[]) {
    return list.map((item) => (
      <Col span={24} md={8} key={`classification-${item.question.id}`}>
        <Card title={item.question.description}>
          <ChartPie
            scheme="red_yellow_green"
            data={[
              {
                id: '1',
                label: '1',
                value: item.quantityOneStarsResponses,
              },
              {
                id: '2',
                label: '2',
                value: item.quantityTwoStarsResponses,
              },
              {
                id: '3',
                label: '3',
                value: item.quantityThreeStarsResponses,
              },
              {
                id: '4',
                label: '4',
                value: item.quantityFourStarsResponses,
              },
              {
                id: '5',
                label: '5',
                value: item.quantityFiveStarsResponses,
              },
            ]}
          />
        </Card>
      </Col>
    ));
  }

  function renderComments(list: CommentBoxResults[]) {
    return list.map((item) => (
      <Col span={24}>
        <Card
          title={item.question.description}
          key={`comments-${item.question.id}`}
        >
          <Table
            dataSource={item.detailingAnswers.map((detail, i) => ({
              key: i,
              customer: detail.customerName,
              answer: detail.textAnswer,
            }))}
            scroll={{ x: true }}
            columns={[
              {
                key: 'customer',
                title: intl.formatMessage({
                  id: 'searchQuestionnaire.syntheticAnalysis.comments.list.customer',
                }),
                dataIndex: 'customer',
                width: 200,
              },
              {
                key: 'answer',
                title: intl.formatMessage({
                  id: 'searchQuestionnaire.syntheticAnalysis.comments.list.answer',
                }),
                dataIndex: 'answer',
                width: 200,
              },
            ]}
            pagination={false}
          />
        </Card>
      </Col>
    ));
  }

  function hasData() {
    return (
      !!data?.commentBoxResults?.length ||
      !!data?.multipleChoiceResponseResults?.length ||
      !!data?.starClassificationResults?.length
    );
  }

  return (
    <>
      <h2 style={{ marginBottom: 24 }}>
        {intl.formatMessage({
          id: 'layout.menu.crm.searchQuestionnaire.details.syntheticAnalysis',
        })}
      </h2>

      <SyntheticAnalysisFilter values={filter} onFilter={setFilter} />

      <Divider />

      <Skeleton loading={isLoading}>
        {!hasData() && (
          <Empty
            description={intl.formatMessage({
              id: 'searchQuestionnaire.emptyDescription',
            })}
          />
        )}

        <Row style={{ marginBottom: 24 }} gutter={[24, 24]}>
          {!!data?.multipleChoiceResponseResults &&
            renderMultipleChoices(data.multipleChoiceResponseResults)}
          {!!data?.starClassificationResults &&
            renderClassification(data.starClassificationResults)}
        </Row>

        <Row style={{ marginBottom: 24 }} gutter={[24, 24]}>
          {!!data?.commentBoxResults && renderComments(data.commentBoxResults)}
        </Row>

        <Divider />

        <SpaceForButton>
          <ButtonBack path="/app/customers/crm/searchQuestionnaire" />
        </SpaceForButton>
      </Skeleton>
    </>
  );
};

export default SyntheticAnalysisPage;
